
import { reportError } from "@/utils/monitoring";
import { toast } from "sonner";

/**
 * Extract error message from different error types
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  } else if (typeof error === 'string') {
    return error;
  } else if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return 'An unknown error occurred';
}

/**
 * Safely parse JSON with error handling
 */
export function safeJsonParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json) as T;
  } catch (error) {
    console.error('Error parsing JSON:', error);
    return fallback;
  }
}

/**
 * Timeout a promise after the specified milliseconds
 */
export function withTimeout<T>(promise: Promise<T>, timeoutMs: number, errorMessage?: string): Promise<T> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error(errorMessage || `Operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]);
}

/**
 * Retry a function with exponential backoff
 */
export async function retry<T>(
  fn: () => Promise<T>,
  retries = 3,
  delay = 300,
  backoffFactor = 2
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 1) throw error;
    
    await new Promise(resolve => setTimeout(resolve, delay));
    return retry(fn, retries - 1, delay * backoffFactor, backoffFactor);
  }
}

/**
 * Show error toast with consistent formatting and report to Sentry
 */
export function showErrorToast(error: unknown, message?: string): void {
  const errorMsg = message ? `${message}: ${getErrorMessage(error)}` : getErrorMessage(error);
  
  // Report to Sentry
  reportError(error, { context: message || 'showErrorToast' });
  
  // Show toast
  toast.error(errorMsg);
  console.error(errorMsg, error);
}

/**
 * Generic error handler for async operations that reports to Sentry
 * @returns An object containing the error message
 */
export function handleError(error: unknown, context: string): { message: string } {
  const errorMsg = `Error in ${context}: ${getErrorMessage(error)}`;
  
  // Report to Sentry with context
  reportError(error, { context });
  
  console.error(errorMsg, error);
  showErrorToast(error, `Error in ${context}`);
  return { message: errorMsg };
}
